import * as React from 'react';
import { createContext, useContext, useState } from 'react';
import { graphql } from 'react-apollo';
import type { DataValue } from 'react-apollo';

import type { EnterprisePermission_FeatureTypePermission as FeatureTypePermission } from '__generated__/graphql-types-do-not-use';
import { compose } from 'recompose';

import user from 'js/lib/user';

import enterpriseAdminUser from 'bundles/enterprise-admin';
import queryDomainLevelPermissions from 'bundles/enterprise-admin-roles/queries/QueryDomainLevelPermissions.graphql';
import querySelectPermissionsUnderDomain from 'bundles/enterprise-admin-roles/queries/QuerySelectPermissionsUnderDomain.graphql';
import type {
  QueryDomainLevelPermissionsQuery,
  QueryDomainLevelPermissionsQueryVariables,
} from 'bundles/enterprise-admin-roles/queries/__generated__/QueryDomainLevelPermissions';
import type {
  QuerySelectPermissionsUnderDomainQuery,
  QuerySelectPermissionsUnderDomainQueryVariables,
} from 'bundles/enterprise-admin-roles/queries/__generated__/QuerySelectPermissionsUnderDomain';
import type {
  EnterpriseAdminPermissionSubdomain,
  EnterpriseAdminPermissionVerb,
  EnterpriseAdminRolePermissions,
  RequiredPermission,
  SubdomainPermissions,
} from 'bundles/enterprise-admin-roles/types';
import {
  ENTERPRISE_ADMIN_PERMISSION_SUBDOMAIN,
  ENTERPRISE_ADMIN_PERMISSION_VERB,
} from 'bundles/enterprise-admin-roles/types';
import filterExistsOrDefault from 'bundles/enterprise-legacy-learner-home/utils/filterExistsOrDefault';
import { LoadingState } from 'bundles/enterprise-ui';
import Naptime from 'bundles/naptimejs';
import PrivilegedAuths from 'bundles/naptimejs/resources/privilegedAuths.v1';
import ThirdPartyOrganizationsV1 from 'bundles/naptimejs/resources/thirdPartyOrganizations.v1';

type PropsFromCaller = {
  thirdPartySlug: string;
  programSlug?: string;
};

type PropsFromNaptimeForOrg = {
  thirdPartyOrganizations?: ThirdPartyOrganizationsV1[];
};

type PropsFromNaptime = {
  privilegedAuths?: { outsourcingPermissions: string[] }[];
};

type PropsFromGraphql = {
  permissions?: EnterpriseAdminRolePermissions;
  areProgramPermissionsLoading?: boolean;
  areOrgPermissionsLoading?: boolean;
  areOrgLevelProgramPermissionsLoading?: boolean;
  orgId?: string;
  refetch?: () => Promise<$TSFixMe>;
};

type PropsFromHOCs = PropsFromGraphql & PropsFromNaptime;

type Props = PropsFromHOCs;

export type EnterpriseAdminPermissionsContextProps = {
  hasPermission: (permission: RequiredPermission) => boolean;
  canViewOrg: boolean;
  currentRequiredPermission?: RequiredPermission;
  setCurrentRequiredPermission: (newRequiredPermission?: RequiredPermission) => void;
  getAllProgramIdsWithPermission: (
    subdomain: EnterpriseAdminPermissionSubdomain,
    verb: EnterpriseAdminPermissionVerb
  ) => string[];
  getIsProgramDomainAdmin: () => boolean;
  getIsOrganizationDomainAdmin: () => boolean;
  onPermissionsUpdated: () => Promise<void>;
};

export const defaultEnterpriseAdminPermissionsContext: EnterpriseAdminPermissionsContextProps = {
  hasPermission: () => false,
  canViewOrg: false,
  currentRequiredPermission: undefined,
  setCurrentRequiredPermission: () => undefined,
  getAllProgramIdsWithPermission: () => [],
  getIsProgramDomainAdmin: () => false,
  getIsOrganizationDomainAdmin: () => false,
  onPermissionsUpdated: () => Promise.resolve(),
};

export const EnterpriseAdminPermissionsContext = createContext<EnterpriseAdminPermissionsContextProps>(
  defaultEnterpriseAdminPermissionsContext
);

export const useEnterpriseAdminPermissions = () => {
  return useContext(EnterpriseAdminPermissionsContext);
};

export const getPhaseOneOverride = (domain?: string, domainId?: string) => {
  // phaseOneOverride is a temporary override in order to give permissions to the existing org and program admin roles until they're replaced
  // with the new permission-based auth roles
  const phaseOneOverride =
    enterpriseAdminUser.canManageOrganization() ||
    enterpriseAdminUser.isSuperUserOrEnterpriseOutsourcing() ||
    (domain === 'PROGRAM' && enterpriseAdminUser.canEditProgram(String(domainId))) ||
    (domain === 'ANY_PROGRAM' && enterpriseAdminUser.canOnlyManagePrograms());
  return phaseOneOverride;
};

export const EnterpriseAdminPermissionsProvider: React.LegacyFunctionComponentWithChildren<Props> = ({
  permissions = {},
  children,
  areOrgLevelProgramPermissionsLoading,
  areOrgPermissionsLoading,
  areProgramPermissionsLoading,
  privilegedAuths,
  orgId,
  refetch,
}) => {
  const [currentRequiredPermission, setCurrentRequiredPermission] = useState<RequiredPermission | undefined>(undefined);
  enterpriseAdminUser.setRoles([], privilegedAuths?.[0]);
  if (areOrgLevelProgramPermissionsLoading || areProgramPermissionsLoading || areOrgPermissionsLoading)
    return <LoadingState data-testid="loading-state" css={{ minHeight: '100vh' }} />;

  const hasSubdomainPermission = (
    subdomain: EnterpriseAdminPermissionSubdomain,
    verb: EnterpriseAdminPermissionVerb
  ) => {
    return Object.values(permissions).some((permissionSubdomain) =>
      Object.entries(permissionSubdomain).some(([key, value]) => key === subdomain && value.has(verb))
    );
  };

  // We're unable to hydrate program-level roles upwards --- program-level roles won't have any org-level permissions
  // In this case, we check to see if any of the program subdomains have the required verb permissions
  const hasPermission = (permission: RequiredPermission) => {
    const { domain, domainId, subdomain = 'DEFAULT_SUBDOMAIN', verb } = permission;

    if (getPhaseOneOverride(domain, domainId)) return true;

    // This handles the case where we check for any program-level permissions, designed for program admins,
    // but the user is an org admin for the org that has no programs
    if (domain === 'ANY_PROGRAM' && Object.keys(permissions).length === 1) {
      return Boolean(permissions?.[Object.keys(permissions)[0]]?.[subdomain]?.has(verb));
    }

    return domainId
      ? Boolean(permissions?.[domainId]?.[subdomain]?.has(verb))
      : hasSubdomainPermission(subdomain, verb);
  };

  const getAllProgramIdsWithPermission = (
    subdomain: EnterpriseAdminPermissionSubdomain,
    verb: EnterpriseAdminPermissionVerb
  ) => {
    if (enterpriseAdminUser.getProgramIdsToManage().length > 0) {
      return enterpriseAdminUser.getProgramIdsToManage();
    }
    return Object.keys(permissions)
      .filter((domainId) => hasPermission({ domain: 'PROGRAM', domainId, subdomain, verb }))
      .filter((domainId) => domainId !== orgId);
  };

  const canViewOrg = Boolean(
    Object.values(permissions).reduce((acc, subdomains) => Object.entries(subdomains).length + acc, 0) > 0
  );

  const getIsProgramDomainAdmin = () => {
    const oldPermissions = enterpriseAdminUser.canOnlyManagePrograms();
    const newPermissions = Boolean(Object.keys(permissions).length > 0 && orgId && !permissions[orgId]);
    return oldPermissions || newPermissions;
  };

  const getIsOrganizationDomainAdmin = () => {
    const oldPermissions = enterpriseAdminUser.canManageOrganization();
    const newPermissions = Boolean(Object.keys(permissions).length > 0 && orgId && permissions[orgId]);
    return oldPermissions || newPermissions;
  };

  const onPermissionsUpdated = () => {
    return refetch?.() ?? Promise.resolve();
  };

  return (
    <EnterpriseAdminPermissionsContext.Provider
      value={{
        currentRequiredPermission,
        canViewOrg,
        getAllProgramIdsWithPermission,
        hasPermission,
        setCurrentRequiredPermission,
        getIsProgramDomainAdmin,
        getIsOrganizationDomainAdmin,
        onPermissionsUpdated,
      }}
    >
      {children}
    </EnterpriseAdminPermissionsContext.Provider>
  );
};

const extractSingleDomainLevelPermissionData = (
  data?: DataValue<QueryDomainLevelPermissionsQuery, QueryDomainLevelPermissionsQueryVariables>
) => {
  const domainPermissionData = data?.EnterprisePermission?.queryDomainLevelPermissions?.[0];
  const allPermissions: FeatureTypePermission[] = filterExistsOrDefault(domainPermissionData?.permissions);
  const domainId = domainPermissionData?.domainObjectIds?.[0] ?? '';

  const domainLevelPermissions = allPermissions.reduce((acc, permission) => {
    if (!permission.subdomain) return acc;

    const setOfVerbs: Set<EnterpriseAdminPermissionVerb> =
      acc?.[permission.subdomain as EnterpriseAdminPermissionSubdomain] ?? new Set<EnterpriseAdminPermissionVerb>();
    return {
      ...acc,
      [permission.subdomain]: setOfVerbs.add(permission.verb as EnterpriseAdminPermissionVerb),
    };
  }, {} as SubdomainPermissions);

  return { domainId, domainLevelPermissions };
};

const extractMultiDomainLevelPermissionData = (
  data?: DataValue<QuerySelectPermissionsUnderDomainQuery, QuerySelectPermissionsUnderDomainQueryVariables>
) => {
  const compressedPermissions = filterExistsOrDefault(data?.EnterprisePermission?.querySelectPermissionsUnderDomain);
  return compressedPermissions.reduce((acc, permissions) => {
    const domainIds = permissions.domainObjectIds;

    const newFormattedPermissions = permissions.permissions.reduce((innerAcc, newPermission) => {
      const subdomainName: EnterpriseAdminPermissionSubdomain =
        (newPermission.subdomain as EnterpriseAdminPermissionSubdomain) ?? 'DEFAULT_SUBDOMAIN';
      const setOfVerbs: Set<EnterpriseAdminPermissionVerb> =
        innerAcc[subdomainName] ?? new Set<EnterpriseAdminPermissionVerb>();
      return {
        ...innerAcc,
        [subdomainName]: setOfVerbs.add(newPermission.verb as EnterpriseAdminPermissionVerb),
      };
    }, {} as SubdomainPermissions);

    // eslint-disable-next-line no-return-assign
    domainIds.forEach((id) => (acc[id] = { ...(acc[id] ?? {}), ...newFormattedPermissions }));
    return acc;
  }, {} as EnterpriseAdminRolePermissions);
};

export const requestedPermissions: QuerySelectPermissionsUnderDomainQueryVariables['permissions'] = [
  { domain: 'PERMISSION_DOMAIN_PROGRAM', subdomain: null, verb: ENTERPRISE_ADMIN_PERMISSION_VERB.VIEW },
  {
    domain: 'PERMISSION_DOMAIN_PROGRAM',
    subdomain: ENTERPRISE_ADMIN_PERMISSION_SUBDOMAIN.COMMUNICATIONS,
    verb: ENTERPRISE_ADMIN_PERMISSION_VERB.VIEW,
  },
  {
    domain: 'PERMISSION_DOMAIN_PROGRAM',
    subdomain: ENTERPRISE_ADMIN_PERMISSION_SUBDOMAIN.COMMUNICATIONS,
    verb: ENTERPRISE_ADMIN_PERMISSION_VERB.MANAGE,
  },
  {
    domain: 'PERMISSION_DOMAIN_PROGRAM',
    subdomain: ENTERPRISE_ADMIN_PERMISSION_SUBDOMAIN.LEARNERS,
    verb: ENTERPRISE_ADMIN_PERMISSION_VERB.MANAGE,
  },
  {
    domain: 'PERMISSION_DOMAIN_PROGRAM',
    subdomain: ENTERPRISE_ADMIN_PERMISSION_SUBDOMAIN.LEARNERS,
    verb: ENTERPRISE_ADMIN_PERMISSION_VERB.VIEW,
  },
  {
    domain: 'PERMISSION_DOMAIN_PROGRAM',
    subdomain: ENTERPRISE_ADMIN_PERMISSION_SUBDOMAIN.ADMINS,
    verb: ENTERPRISE_ADMIN_PERMISSION_VERB.VIEW,
  },
  {
    domain: 'PERMISSION_DOMAIN_PROGRAM',
    subdomain: ENTERPRISE_ADMIN_PERMISSION_SUBDOMAIN.USAGE_REPORT,
    verb: ENTERPRISE_ADMIN_PERMISSION_VERB.EXPORT,
  },
  {
    domain: 'PERMISSION_DOMAIN_PROGRAM',
    subdomain: ENTERPRISE_ADMIN_PERMISSION_SUBDOMAIN.CONTENT_ASSIGNMENT,
    verb: ENTERPRISE_ADMIN_PERMISSION_VERB.VIEW,
  },
  {
    domain: 'PERMISSION_DOMAIN_PROGRAM',
    subdomain: ENTERPRISE_ADMIN_PERMISSION_SUBDOMAIN.CONTENT_ASSIGNMENT,
    verb: ENTERPRISE_ADMIN_PERMISSION_VERB.EDIT,
  },
  {
    domain: 'PERMISSION_DOMAIN_PROGRAM',
    subdomain: ENTERPRISE_ADMIN_PERMISSION_SUBDOMAIN.GRADEBOOK,
    verb: ENTERPRISE_ADMIN_PERMISSION_VERB.EXPORT,
  },
  {
    domain: 'PERMISSION_DOMAIN_PROGRAM',
    subdomain: ENTERPRISE_ADMIN_PERMISSION_SUBDOMAIN.ANALYTICS,
    verb: ENTERPRISE_ADMIN_PERMISSION_VERB.VIEW,
  },
  {
    domain: 'PERMISSION_DOMAIN_PROGRAM',
    subdomain: ENTERPRISE_ADMIN_PERMISSION_SUBDOMAIN.SKILLSETS,
    verb: ENTERPRISE_ADMIN_PERMISSION_VERB.VIEW,
  },
  {
    domain: 'PERMISSION_DOMAIN_PROGRAM',
    subdomain: ENTERPRISE_ADMIN_PERMISSION_SUBDOMAIN.CURRICULUM,
    verb: ENTERPRISE_ADMIN_PERMISSION_VERB.EDIT,
  },
  {
    domain: 'PERMISSION_DOMAIN_PROGRAM',
    subdomain: ENTERPRISE_ADMIN_PERMISSION_SUBDOMAIN.CURRICULUM,
    verb: ENTERPRISE_ADMIN_PERMISSION_VERB.VIEW,
  },
  {
    domain: 'PERMISSION_DOMAIN_PROGRAM',
    subdomain: ENTERPRISE_ADMIN_PERMISSION_SUBDOMAIN.SKILLSETS,
    verb: ENTERPRISE_ADMIN_PERMISSION_VERB.EDIT,
  },
  {
    domain: 'PERMISSION_DOMAIN_PROGRAM',
    subdomain: ENTERPRISE_ADMIN_PERMISSION_SUBDOMAIN.LEVELSETS,
    verb: ENTERPRISE_ADMIN_PERMISSION_VERB.VIEW,
  },
  {
    domain: 'PERMISSION_DOMAIN_PROGRAM',
    subdomain: ENTERPRISE_ADMIN_PERMISSION_SUBDOMAIN.SETTINGS,
    verb: ENTERPRISE_ADMIN_PERMISSION_VERB.EDIT,
  },
  {
    domain: 'PERMISSION_DOMAIN_PROGRAM',
    subdomain: null,
    verb: ENTERPRISE_ADMIN_PERMISSION_VERB.ARCHIVE,
  },
  {
    domain: 'PERMISSION_DOMAIN_ORGANIZATION',
    subdomain: ENTERPRISE_ADMIN_PERMISSION_SUBDOMAIN.PROGRAM,
    verb: ENTERPRISE_ADMIN_PERMISSION_VERB.CREATE,
  },
  {
    domain: 'PERMISSION_DOMAIN_ORGANIZATION',
    subdomain: ENTERPRISE_ADMIN_PERMISSION_SUBDOMAIN.USAGE_REPORT,
    verb: ENTERPRISE_ADMIN_PERMISSION_VERB.EXPORT,
  },
  {
    domain: 'PERMISSION_DOMAIN_PROGRAM',
    subdomain: ENTERPRISE_ADMIN_PERMISSION_SUBDOMAIN.CUSTOM_COURSES,
    verb: ENTERPRISE_ADMIN_PERMISSION_VERB.VIEW,
  },
  {
    domain: 'PERMISSION_DOMAIN_PROGRAM',
    subdomain: ENTERPRISE_ADMIN_PERMISSION_SUBDOMAIN.CUSTOM_COURSES,
    verb: ENTERPRISE_ADMIN_PERMISSION_VERB.EDIT,
  },
  {
    domain: 'PERMISSION_DOMAIN_ORGANIZATION',
    subdomain: ENTERPRISE_ADMIN_PERMISSION_SUBDOMAIN.SSO,
    verb: ENTERPRISE_ADMIN_PERMISSION_VERB.MANAGE,
  },
];

const withOrgLevelProgramAdminPermissions = graphql<
  PropsFromCaller & PropsFromGraphql,
  QuerySelectPermissionsUnderDomainQuery,
  QuerySelectPermissionsUnderDomainQueryVariables,
  PropsFromGraphql
>(querySelectPermissionsUnderDomain, {
  skip: ({ thirdPartySlug }) => !thirdPartySlug || !user.get().id,
  options: ({ thirdPartySlug }) => ({
    variables: {
      domainSlug: { organizationSlug: thirdPartySlug },
      userId: user.get().id,
      permissions: requestedPermissions,
    },
    errorPolicy: 'all',
    context: { clientName: 'gatewayGql' },
  }),
  props: ({ data, ownProps = {} }) => {
    if (data?.error) return {};
    const { permissions = {} } = ownProps;
    return {
      permissions: {
        ...permissions,
        ...extractMultiDomainLevelPermissionData(data),
      },
      areOrgLevelProgramPermissionsLoading: Boolean(data?.loading),
      refetch: data?.refetch,
    };
  },
});

const withOrgLevelAdminRolePermissions = graphql<
  PropsFromCaller & PropsFromGraphql,
  QueryDomainLevelPermissionsQuery,
  QueryDomainLevelPermissionsQueryVariables,
  PropsFromGraphql
>(queryDomainLevelPermissions, {
  skip: ({ thirdPartySlug, permissions }) => !thirdPartySlug || !user.get().id || !permissions,
  options: ({ thirdPartySlug }) => ({
    variables: { domainSlug: { organizationSlug: thirdPartySlug }, userId: user.get().id },
    context: { clientName: 'gatewayGql' },
    errorPolicy: 'all',
  }),
  props: ({ data, ownProps = {} }) => {
    if (data?.error) return {};

    const { permissions, refetch } = ownProps;
    const { domainId, domainLevelPermissions } = extractSingleDomainLevelPermissionData(data);

    // Admin permissions can be hydrated on the program or org level. If the role was hydrated on the program level, we're not able to
    // assign permissions upwards. The org-level permissions will always be empty in this case
    const orgLevelPermissions = domainId ? { [domainId]: domainLevelPermissions } : {};

    return {
      permissions: { ...permissions, ...orgLevelPermissions },
      areOrgPermissionsLoading: Boolean(data?.loading),
      orgId: domainId,
      refetch: () => {
        return Promise.all([refetch?.(), data?.refetch()]);
      },
    };
  },
});

const withProgramLevelAdminRolePermissions = graphql<
  PropsFromCaller & PropsFromGraphql,
  QueryDomainLevelPermissionsQuery,
  QueryDomainLevelPermissionsQueryVariables,
  PropsFromGraphql
>(queryDomainLevelPermissions, {
  skip: ({ programSlug, thirdPartySlug, permissions }) =>
    !thirdPartySlug || !user.get().id || !programSlug || !permissions,
  options: ({ programSlug }) => ({
    variables: { domainSlug: { programSlug: programSlug! }, userId: user.get().id },
    context: { clientName: 'gatewayGql' },
    notifyOnNetworkStatusChange: true,
    errorPolicy: 'all',
  }),
  props: ({ data, ownProps = {} }) => {
    if (data?.error) return {};
    const { domainId, domainLevelPermissions } = extractSingleDomainLevelPermissionData(data);
    const { permissions: orgLevelPermissions = {}, refetch } = ownProps;

    const permissions = {
      ...orgLevelPermissions,
      ...(domainId ? { [domainId]: { ...(orgLevelPermissions[domainId] ?? {}), ...domainLevelPermissions } } : {}),
    };

    return {
      permissions,
      areProgramPermissionsLoading: Boolean(data?.loading),
      refetch: () => {
        return Promise.all([refetch?.(), data?.refetch()]);
      },
    };
  },
});

const withOrg = Naptime.createContainer<PropsFromNaptimeForOrg, PropsFromCaller>(({ thirdPartySlug }) => ({
  thirdPartyOrganizations:
    user.isAuthenticatedUser() && thirdPartySlug
      ? ThirdPartyOrganizationsV1.finder('bySlug', {
          params: { slug: thirdPartySlug },
          fields: ['slug', 'name', 'id'],
        })
      : [],
}));

const withLegacyPermissions = Naptime.createContainer<PropsFromNaptime, PropsFromCaller & PropsFromNaptimeForOrg>(
  ({}) => {
    return {
      privilegedAuths: user.isAuthenticatedUser()
        ? PrivilegedAuths.finder('me', {
            fields: ['outsourcingPermissions'],
            required: false,
          })
        : [],
    };
  }
);

export default compose<PropsFromHOCs & PropsFromCaller, React.PropsWithChildren<PropsFromCaller>>(
  withOrgLevelProgramAdminPermissions,
  withOrgLevelAdminRolePermissions,
  withProgramLevelAdminRolePermissions,
  withOrg,
  withLegacyPermissions
)(EnterpriseAdminPermissionsProvider);
